'use client';
import { _ as _object_without_properties_loose, a as _extends } from './cc-CRoTJfvR.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@repo/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';

const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-1 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
    variants: {
        variant: {
            default: "bg-primary text-primary-foreground shadow-xs focus-within:bg-primary/90 hover:bg-primary/90",
            destructive: "bg-destructive text-white shadow-xs focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 focus-within:bg-destructive/90 hover:bg-destructive/90",
            outline: "border bg-background shadow-xs focus-within:bg-accent hover:bg-accent focus-within:text-accent-foreground hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:focus-within:bg-input/50 dark:hover:bg-input/50",
            secondary: "bg-secondary text-secondary-foreground shadow-xs focus-within:bg-secondary/80 hover:bg-secondary/80",
            ghost: "focus-within:bg-accent focus-within:text-accent-foreground dark:focus-within:bg-accent/50 dark:hover:bg-accent/50",
            link: "text-primary underline-offset-4 focus-visible:underline hover:underline",
            icon: "focus-visible:[&_svg]:fill-accent hover:[&_svg]:fill-accent focus-visible:ring-none focus-visible:ring-0 hover:ring-0"
        },
        size: {
            default: "h-9 px-4 py-2 has-[>svg]:px-3",
            sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
            lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
            icon: "size-9"
        }
    },
    defaultVariants: {
        variant: "default",
        size: "default"
    }
});
function Button(_param) {
    var { className, variant, size, asChild = false } = _param, props = _object_without_properties_loose(_param, [
        "className",
        "variant",
        "size",
        "asChild"
    ]);
    const Comp = asChild ? Slot : "button";
    return /*#__PURE__*/ jsx(Comp, _extends({
        "data-slot": "button",
        className: cn(buttonVariants({
            variant,
            size,
            className
        }))
    }, props));
}

const CarouselContext = /*#__PURE__*/ React.createContext(null);
function useCarousel() {
    const context = React.useContext(CarouselContext);
    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }
    return context;
}
function Carousel(_param) {
    var { orientation = "horizontal", opts, setApi, plugins, className, children } = _param, props = _object_without_properties_loose(_param, [
        "orientation",
        "opts",
        "setApi",
        "plugins",
        "className",
        "children"
    ]);
    const [carouselRef, api] = useEmblaCarousel(_extends({}, opts, {
        axis: orientation === "horizontal" ? "x" : "y"
    }), plugins);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const onSelect = React.useCallback((api)=>{
        if (!api) return;
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);
    const scrollPrev = React.useCallback(()=>{
        api == null ? void 0 : api.scrollPrev();
    }, [
        api
    ]);
    const scrollNext = React.useCallback(()=>{
        api == null ? void 0 : api.scrollNext();
    }, [
        api
    ]);
    const handleKeyDown = React.useCallback((event)=>{
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            scrollPrev();
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            scrollNext();
        }
    }, [
        scrollPrev,
        scrollNext
    ]);
    React.useEffect(()=>{
        if (!api || !setApi) return;
        setApi(api);
    }, [
        api,
        setApi
    ]);
    React.useEffect(()=>{
        if (!api) return;
        onSelect(api);
        api.on("reInit", onSelect);
        api.on("select", onSelect);
        return ()=>{
            api == null ? void 0 : api.off("select", onSelect);
        };
    }, [
        api,
        onSelect
    ]);
    return /*#__PURE__*/ jsx(CarouselContext.Provider, {
        value: {
            carouselRef,
            api: api,
            opts,
            orientation: orientation || ((opts == null ? void 0 : opts.axis) === "y" ? "vertical" : "horizontal"),
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext
        },
        children: /*#__PURE__*/ jsx("div", _extends({
            onKeyDownCapture: handleKeyDown,
            className: cn("relative", className),
            role: "region",
            "aria-roledescription": "carousel",
            "data-slot": "carousel"
        }, props, {
            children: children
        }))
    });
}
function CarouselContent(_param) {
    var { className } = _param, props = _object_without_properties_loose(_param, [
        "className"
    ]);
    const { carouselRef, orientation } = useCarousel();
    return /*#__PURE__*/ jsx("div", {
        ref: carouselRef,
        className: "overflow-hidden",
        "data-slot": "carousel-content",
        children: /*#__PURE__*/ jsx("div", _extends({
            className: cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)
        }, props))
    });
}
function CarouselItem(_param) {
    var { className } = _param, props = _object_without_properties_loose(_param, [
        "className"
    ]);
    const { orientation } = useCarousel();
    return /*#__PURE__*/ jsx("div", _extends({
        role: "group",
        "aria-roledescription": "slide",
        "data-slot": "carousel-item",
        className: cn("min-w-0 shrink-0 grow-0 basis-full", orientation === "horizontal" ? "pl-4" : "pt-4", className)
    }, props));
}
function CarouselPrevious(_param) {
    var { className, variant = "outline", size = "icon" } = _param, props = _object_without_properties_loose(_param, [
        "className",
        "variant",
        "size"
    ]);
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return /*#__PURE__*/ jsxs(Button, _extends({
        "data-slot": "carousel-previous",
        variant: variant,
        size: size,
        className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 left-4 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90", className),
        disabled: !canScrollPrev,
        onClick: scrollPrev
    }, props, {
        children: [
            /*#__PURE__*/ jsx(ArrowLeftIcon, {}),
            /*#__PURE__*/ jsx("span", {
                className: "sr-only",
                children: "Previous slide"
            })
        ]
    }));
}
function CarouselNext(_param) {
    var { className, variant = "outline", size = "icon" } = _param, props = _object_without_properties_loose(_param, [
        "className",
        "variant",
        "size"
    ]);
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return /*#__PURE__*/ jsxs(Button, _extends({
        "data-slot": "carousel-next",
        variant: variant,
        size: size,
        className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 right-4 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90", className),
        disabled: !canScrollNext,
        onClick: scrollNext
    }, props, {
        children: [
            /*#__PURE__*/ jsx(ArrowRightIcon, {}),
            /*#__PURE__*/ jsx("span", {
                className: "sr-only",
                children: "Next slide"
            })
        ]
    }));
}

export { Button as B, Carousel as C, CarouselContent as a, buttonVariants as b, CarouselItem as c, CarouselPrevious as d, CarouselNext as e };
